import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { WebsiteLayout } from '@/components/website/WebsiteLayout';
import { Shield, Target, Eye, Users, Award, CheckCircle, ArrowRight, Phone, Sparkles, Heart, Building2, TrendingUp } from 'lucide-react';
import api from '@/utils/api';

const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { el.classList.add('visible'); obs.unobserve(el); } }, { threshold: 0.12 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
};
const Reveal = ({ children, className = '', dir = 'up', delay = 0 }) => {
  const ref = useReveal();
  const cls = dir === 'left' ? 'reveal-left' : dir === 'right' ? 'reveal-right' : dir === 'scale' ? 'reveal-scale' : 'reveal';
  return <div ref={ref} className={`${cls} ${delay ? `stagger-${delay}` : ''} ${className}`}>{children}</div>;
};

const Counter = ({ target, suffix = '' }) => {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const num = parseInt(String(target).replace(/[^0-9]/g, '')) || 0;
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        let s = 0; const step = Math.ceil(num / 40);
        const t = setInterval(() => { s += step; if (s >= num) { setVal(num); clearInterval(t); } else setVal(s); }, 30);
        obs.unobserve(e.target);
      }
    }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [num]);
  return <span ref={ref}>{val}{suffix}</span>;
};

export default function AboutPage() {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get('/public/website-settings').then(r => setSettings(r.data)).catch(() => {});
  }, []);

  return (
    <WebsiteLayout>
      <Helmet>
        <title>{settings?.company_name ? `About Us | ${settings.company_name}` : 'About Us | Ganesa Homestate Developers'}</title>
        <meta name="description" content={settings?.about_content?.substring(0, 160) || 'Ganesa Homestate Developers - 10+ years of trust in real estate. 50+ projects, 1000+ happy customers.'} />
        {settings?.meta_keywords && <meta name="keywords" content={settings.meta_keywords} />}
      </Helmet>

      {/* Hero */}
      <section className="relative bg-[#0A0A0F] py-16 sm:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#DAA520]/5 via-transparent to-[#DAA520]/3" />
        <div className="absolute top-20 right-20 w-72 h-72 bg-[#DAA520]/5 rounded-full blur-3xl animate-float-slow hidden lg:block" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-[2px] bg-[#DAA520]" />
              <span className="text-[#DAA520] text-xs sm:text-sm font-bold uppercase tracking-[0.2em]">About Us</span>
            </div>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.1] mb-6">
              Building Dreams,<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#DAA520] via-[#FFD700] to-[#DAA520] animate-gradient">Delivering Trust</span>
            </h1>
            <p className="text-slate-400 text-sm sm:text-lg leading-relaxed max-w-2xl">
              {settings?.about_content || 'Ganesa Homestate Pvt. Ltd. ek leading real estate company hai jo premium properties complete transparency ke saath deliver karti hai.'}
            </p>
          </div>
        </div>
      </section>

      {/* Stats Counter Band */}
      <section className="bg-gradient-to-r from-[#DAA520] via-[#FFD700] to-[#DAA520] py-6 sm:py-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMwQjExMjAiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE4YzMuMzE0IDAgNi0yLjY4NiA2LTZzLTIuNjg2LTYtNi02LTYgMi42ODYtNiA2IDIuNjg2IDYgNiA2em0wIDM2YzMuMzE0IDAgNi0yLjY4NiA2LTZzLTIuNjg2LTYtNi02LTYgMi42ODYtNiA2IDIuNjg2IDYgNiA2eiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
          <div className="grid grid-cols-4 gap-3 sm:gap-8">
            {[
              { val: settings?.stats?.projects || '50+', label: 'Projects', icon: Building2 },
              { val: settings?.stats?.customers || '1000+', label: 'Happy Families', icon: Heart },
              { val: settings?.stats?.years || '10+', label: 'Years Experience', icon: Award },
              { val: settings?.stats?.cities || '5+', label: 'Cities', icon: TrendingUp },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <div className="text-2xl sm:text-4xl lg:text-5xl font-extrabold text-[#0A0A0F] mb-0.5">
                  <Counter target={s.val} suffix="+" />
                </div>
                <div className="text-[9px] sm:text-sm text-[#0A0A0F]/70 uppercase tracking-wider font-semibold">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-14 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-6 sm:gap-10">
            <Reveal dir="left">
              <div className="group bg-white rounded-3xl p-6 sm:p-10 shadow-sm hover:shadow-2xl transition-all duration-500 border border-slate-100 hover-lift relative overflow-hidden">
                <div className="absolute top-0 left-0 w-1.5 h-full bg-gradient-to-b from-[#DAA520] to-[#B8860B] rounded-r-full" />
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#0A0A0F] to-[#1A1A25] flex items-center justify-center mb-5 group-hover:scale-110 transition-transform shadow-lg">
                  <Target size={24} className="text-[#DAA520]" />
                </div>
                <h2 className="text-xl sm:text-2xl font-extrabold text-[#0A0A0F] mb-4">Our Mission</h2>
                <p className="text-slate-600 leading-relaxed text-sm sm:text-base">{settings?.mission || 'World-class real estate solutions complete transparency aur trust ke saath dena, har family ke liye property ownership accessible banana.'}</p>
              </div>
            </Reveal>
            <Reveal dir="right">
              <div className="group bg-white rounded-3xl p-6 sm:p-10 shadow-sm hover:shadow-2xl transition-all duration-500 border border-slate-100 hover-lift relative overflow-hidden">
                <div className="absolute top-0 right-0 w-1.5 h-full bg-gradient-to-b from-[#DAA520] to-[#B8860B] rounded-l-full" />
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#0A0A0F] to-[#1A1A25] flex items-center justify-center mb-5 group-hover:scale-110 transition-transform shadow-lg">
                  <Eye size={24} className="text-[#DAA520]" />
                </div>
                <h2 className="text-xl sm:text-2xl font-extrabold text-[#0A0A0F] mb-4">Our Vision</h2>
                <p className="text-slate-600 leading-relaxed text-sm sm:text-base">{settings?.vision || 'India me real estate development ka sabse trusted naam banana - quality, integrity, aur customer satisfaction ke liye.'}</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-14 sm:py-24 bg-[#FAFBFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <Reveal>
            <div className="text-center mb-10 sm:mb-16">
              <div className="inline-flex items-center gap-2 mb-3">
                <div className="w-8 h-[2px] bg-[#DAA520]" /><span className="text-[#DAA520] text-xs sm:text-sm font-bold uppercase tracking-[0.2em]">Our Values</span><div className="w-8 h-[2px] bg-[#DAA520]" />
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#0A0A0F]">What Drives Us</h2>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-5 sm:gap-8">
            {[
              { icon: Shield, title: 'Transparency', desc: 'Har transaction me complete honesty. Clear titles, proper documentation, aur koi hidden charges nahi.', color: 'from-emerald-500 to-emerald-700' },
              { icon: Award, title: 'Quality', desc: 'Premium construction quality modern amenities ke saath. Har project highest standards ko meet karta hai.', color: 'from-amber-500 to-amber-700' },
              { icon: Users, title: 'Customer First', desc: 'Aapki satisfaction hamari priority hai. Booking se possession tak dedicated support.', color: 'from-blue-500 to-blue-700' },
            ].map((v, i) => (
              <Reveal key={i} dir="scale" delay={i + 1}>
                <div className="group bg-white rounded-3xl p-6 sm:p-8 text-center shadow-sm hover:shadow-2xl transition-all duration-500 border border-slate-100 hover-lift relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-b from-[#DAA520]/0 to-[#DAA520]/0 group-hover:to-[#DAA520]/5 transition-all duration-500 rounded-3xl" />
                  <div className={`relative w-16 h-16 rounded-2xl bg-gradient-to-br ${v.color} flex items-center justify-center mx-auto mb-5 group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-lg`}>
                    <v.icon size={28} className="text-white" />
                  </div>
                  <h3 className="relative font-extrabold text-lg sm:text-xl text-[#0A0A0F] mb-3">{v.title}</h3>
                  <p className="relative text-slate-500 text-sm leading-relaxed">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      {settings?.team_members && settings.team_members.length > 0 && (
        <section className="py-14 sm:py-24 bg-white" data-testid="team-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <Reveal><div className="text-center mb-10 sm:mb-16">
              <div className="inline-flex items-center gap-2 mb-3">
                <div className="w-8 h-[2px] bg-[#DAA520]" /><span className="text-[#DAA520] text-xs sm:text-sm font-bold uppercase tracking-[0.2em]">Our Team</span><div className="w-8 h-[2px] bg-[#DAA520]" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0A0A0F]">Meet the Leaders</h2>
            </div></Reveal>
            <div className="grid md:grid-cols-3 gap-6">
              {settings.team_members.map((m, i) => (
                <Reveal key={i} delay={i + 1}>
                  <div className="group text-center bg-white rounded-3xl p-6 sm:p-8 shadow-sm hover:shadow-2xl border border-slate-100 hover-lift transition-all duration-500">
                    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#DAA520] to-[#B8860B] mx-auto mb-4 flex items-center justify-center text-3xl font-bold text-[#0A0A0F] group-hover:scale-110 transition-transform shadow-xl">
                      {(m.name || 'T')[0]}
                    </div>
                    <h3 className="font-bold text-lg text-[#0A0A0F]">{m.name}</h3>
                    <p className="text-[#DAA520] text-sm font-semibold">{m.designation}</p>
                    {m.bio && <p className="text-slate-500 text-sm mt-3">{m.bio}</p>}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Services */}
      {settings?.services && settings.services.length > 0 && (
        <section className="py-14 sm:py-24 bg-[#FAFBFC]" data-testid="about-services-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <Reveal><div className="text-center mb-10 sm:mb-16">
              <div className="inline-flex items-center gap-2 mb-3">
                <div className="w-8 h-[2px] bg-[#DAA520]" /><span className="text-[#DAA520] text-xs sm:text-sm font-bold uppercase tracking-[0.2em]">Our Services</span><div className="w-8 h-[2px] bg-[#DAA520]" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0A0A0F]">What We Offer</h2>
            </div></Reveal>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {settings.services.map((s, i) => (
                <Reveal key={i} delay={Math.min(i + 1, 6)}>
                  <div className="group bg-white rounded-2xl p-5 sm:p-6 shadow-sm hover:shadow-2xl transition-all duration-500 border border-slate-100 hover-lift relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#DAA520] to-[#FFD700] scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#DAA520] to-[#B8860B] flex items-center justify-center mb-3 text-[#0A0A0F] font-bold shadow-md group-hover:scale-110 transition-transform">{i + 1}</div>
                    <h3 className="font-bold text-base text-[#0A0A0F] mb-1">{s.title}</h3>
                    {s.description && <p className="text-slate-500 text-sm leading-relaxed">{s.description}</p>}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Why Ganesa */}
      <section className="py-14 sm:py-24 bg-[#0A0A0F] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#DAA520]/5 via-transparent to-[#DAA520]/3" />
        <div className="absolute bottom-10 right-10 w-60 h-60 bg-[#DAA520]/5 rounded-full blur-3xl animate-float hidden lg:block" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
          <Reveal>
            <div className="text-center mb-10 sm:mb-14">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white">Why <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#DAA520] to-[#FFD700]">Ganesa Homestate?</span></h2>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-4 max-w-3xl mx-auto">
            {[
              'Clear title projects with proper documentation',
              'Flexible payment plans aur easy EMI options',
              'Prime locations with high appreciation potential',
              'Har transaction me complete transparency',
              'Post-sale support aur property management',
              '1000+ happy families ne trust kiya hai',
            ].map((item, i) => (
              <Reveal key={i} delay={Math.min(i + 1, 6)}>
                <div className="flex items-center gap-4 bg-[#1A1A25] rounded-xl p-4 border border-[#1A1A25] hover:border-[#DAA520]/30 transition-all duration-300 group hover:bg-[#1A1A25]/80">
                  <div className="w-8 h-8 rounded-lg bg-[#DAA520]/10 flex items-center justify-center shrink-0 group-hover:bg-[#DAA520] group-hover:scale-110 transition-all duration-300">
                    <CheckCircle size={16} className="text-[#DAA520] group-hover:text-[#0A0A0F] transition-colors" />
                  </div>
                  <span className="text-slate-300 text-sm font-medium">{item}</span>
                </div>
              </Reveal>
            ))}
          </div>

          {/* CTA */}
          <Reveal>
            <div className="text-center mt-10 sm:mt-14">
              <div className="flex flex-col sm:flex-row justify-center gap-3">
                <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-[#DAA520] to-[#B8860B] text-[#0A0A0F] font-bold rounded-xl hover:shadow-[0_8px_40px_rgba(201,168,76,0.4)] transition-all hover:scale-105 text-sm animate-pulse-gold">
                  <Sparkles size={16} /> Free Consultation
                </Link>
                <Link to="/properties" className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-[#DAA520]/30 text-[#DAA520] font-bold rounded-xl hover:bg-[#DAA520]/10 transition-all text-sm">
                  View Properties <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </WebsiteLayout>
  );
}
